import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';

interface AppChoicePageProps {
  onContinueInBrowser: () => void;
}

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: 16,
    backgroundColor: 'white',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 320,
    width: '100%',
    textAlign: 'center',
  },
  icon: {
    width: 100, 
    height: 100,
    marginBottom: 32,
    borderRadius: '16px',
    border: '1px solid #E5E7EB',
  },
  titleGroup: {
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: 600,
    margin: '0 0 2px',
    color: '#1F2152',
  },
  subtitle: {
    fontSize: 16,
    color: '#5A6A81',
    fontWeight: 400,
    margin: 0,
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    alignItems: 'center',
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 240,
    height: 80,
    textDecoration: 'none',
    transition: 'opacity 0.2s',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    '&:hover': {
      opacity: 0.8,
    },
  },
  storeBadge: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  browserButton: {
    width: 240,
    height: 70,
    textDecoration: 'none',
    transition: 'opacity 0.2s',
    cursor: 'pointer',
    background: 'none',
    color: '#1F2152',
    fontSize: 16,
    fontWeight: 600,
    marginTop: 10,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid #A6A6BF',
    borderRadius: 10,
    '&:hover': {
      opacity: 0.8,
    },
  },
}));

const isIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

const isAndroid = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
};

export const AppChoicePage: React.FC<AppChoicePageProps> = ({ onContinueInBrowser }) => {
  const classes = useStyles();
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);

  useEffect(() => {
    if (isIOS()) {
      setPlatform('ios');
    } else if (isAndroid()) {
      setPlatform('android');
    } else {
      // Default to iOS for desktop/other devices
      setPlatform('ios');
    }
  }, []);

  const handleAppStore = () => {
    navigator.clipboard.writeText(window.location.href);
    window.open('https://apps.apple.com/us/app/samaritan-partner/id1101216040', '_blank');
  };

  const handlePlayStore = () => {
    window.open('https://play.google.com/store/apps/details?id=org.givesafe.merchant&hl=en', '_blank');
  };

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <img 
          src="/partner-app-icon.png" 
          alt="App Icon"
          className={classes.icon}
        />
        
        <div className={classes.titleGroup}>
          <h1 className={classes.title}>Welcome to Samaritan</h1>
          <p className={classes.subtitle}>Choose how you'd like to get started:</p>
        </div>
        
        <div className={classes.buttonGroup}>
          {platform === 'ios' ? (
            <button onClick={handleAppStore} className={classes.button}>
              <img 
                src="/app-store-badge.svg" 
                alt="Download on the App Store"
                className={classes.storeBadge}
              />
            </button>
          ) : (
            <button onClick={handlePlayStore} className={classes.button}>
              <img 
                src="/play-store-badge.png" 
                alt="Get it on Google Play"
                className={classes.storeBadge}
              />
            </button>
          )}

          <button onClick={onContinueInBrowser} className={classes.browserButton}>
            Continue in Browser
          </button>
        </div>
      </div>
    </div>
  );
};
